import React from 'react'
import { yupResolver } from '@hookform/resolvers/yup'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import BasicButton from '@divvy-web/skylab.basicbutton'
import { FormPhoneInput, FormTextInput } from '@divvy-web/skylab.form'
import { Control, useForm, useFormState } from '@divvy-web/skylab.react-hook-form'

import { useAuth } from '@sputnik/auth'
import AccountingSoftwareDropdown from '@sputnik/components/AccountingSoftwareDropdown'
import PersonJobTitleDropdown from '@sputnik/components/PersonJobTitleDropdown'
import { formatPhoneNumberFormField, limitInputLength } from '@sputnik/components/utils'
import useInitCreditApplication from '@sputnik/pages/PreApplicationPages/useInitCreditApplication'
import { AddPersonForm } from '@sputnik/schema/creditApplication/addPerson'
import { type InitCreditApplication, initCreditApplication } from '@sputnik/schema/creditApplication/init'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

import styles from './styles.module.scss'

export const GetToKnowYouForm = () => {
  const [getClassName] = useNameFormatter('GetToKnowYouForm')
  const { email } = useAuth()

  const { applicantData } = useWebTracking()
  const { handleInitCreditApplication, isLoadingInitCreditApplication } = useInitCreditApplication()

  const {
    email: queryEmail,
    firstName,
    lastName,
    phone,
    legalBusinessName,
    companyName,
    numberOfEmployees,
    accountingSoftware,
    title,
  } = applicantData ?? {}

  const { control, handleSubmit } = useForm<InitCreditApplication>({
    mode: 'all',
    defaultValues: {
      email: queryEmail ? queryEmail : email || '',
      firstName: firstName || '',
      lastName: lastName || '',
      phoneNumber: formatPhoneNumberFormField(phone || '') || '',
      legalBusinessName: legalBusinessName || companyName || '',
      numberOfEmployees: numberOfEmployees ? Number(numberOfEmployees) : 0,
      accountingSoftware: accountingSoftware || '',
      title: title || '',
    },
    resolver: yupResolver(initCreditApplication),
  })

  const { isValid, isSubmitting } = useFormState({ control })
  const shouldDisable = isSubmitting || isLoadingInitCreditApplication

  const onSubmit = async (data: InitCreditApplication) => {
    await handleInitCreditApplication(data)
  }

  return (
    <form
      className={`${styles[getClassName('')]} dd-privacy-allow`}
      onSubmit={handleSubmit(onSubmit)}
    >
      <PersonJobTitleDropdown
        // @ts-expect-error: Type narrowing for control to satisfy PersonJobTitleDropdown
        control={control as Control<Pick<AddPersonForm, 'title'>>}
        disabled={shouldDisable}
        label={
          <FormattedMessage
            defaultMessage='Your role'
            id='sputnik.GetToKnowYouForm__8UHLr/'
          />
        }
      />
      <div className={styles[getClassName('row')]}>
        <FormTextInput
          className='dd-privacy-mask'
          control={control}
          disabled={shouldDisable}
          label={
            <FormattedMessage
              defaultMessage='Your first name'
              id='sputnik.GetToKnowYouForm__x5rks2'
            />
          }
          name='firstName'
        />
        <FormTextInput
          className='dd-privacy-mask'
          control={control}
          disabled={shouldDisable}
          label={
            <FormattedMessage
              defaultMessage='Your last name'
              id='sputnik.GetToKnowYouForm__FuI6qb'
            />
          }
          name='lastName'
        />
      </div>
      <div className={styles[getClassName('row')]}>
        <FormPhoneInput
          className='dd-privacy-mask'
          control={control}
          disabled={shouldDisable}
          label={
            <FormattedMessage
              defaultMessage='Your phone number'
              id='sputnik.GetToKnowYouForm__scvoKh'
            />
          }
          name='phoneNumber'
        />
        <FormTextInput
          readOnly
          className='dd-privacy-mask'
          control={control}
          label={
            <FormattedMessage
              defaultMessage='Your email'
              id='sputnik.GetToKnowYouForm__nONnTw'
            />
          }
          name='email'
        />
      </div>
      <div className={styles[getClassName('row')]}>
        <FormTextInput
          control={control}
          disabled={shouldDisable}
          label={
            <FormattedMessage
              defaultMessage='Legal business name'
              id='sputnik.GetToKnowYouForm__U/+zt/'
            />
          }
          name='legalBusinessName'
        />
        <FormTextInput
          control={control}
          disabled={shouldDisable}
          label={
            <FormattedMessage
              defaultMessage='Number of employees'
              id='sputnik.GetToKnowYouForm__cErP2u'
            />
          }
          name='numberOfEmployees'
          type='number'
          onInput={(e) => limitInputLength(e, 6)}
        />
      </div>
      <div className={styles[getClassName('accounting-software')]}>
        <AccountingSoftwareDropdown
          control={control}
          disabled={shouldDisable}
        />
      </div>
      <div className={styles[getClassName('submit')]}>
        <BasicButton
          buttonType='submit'
          disabled={!isValid || shouldDisable}
          showSpinner={shouldDisable}
        >
          <FormattedMessage
            defaultMessage='Continue'
            id='sputnik.GetToKnowYouForm__acrOoz'
          />
        </BasicButton>
      </div>
    </form>
  )
}

export default GetToKnowYouForm
